import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { myRiskQueries } from '../../lib/queries';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import VendorDetails, { T, sans } from '../Vendors/VendorDetails';

type Conflict = { vendor_id: string; vendor_name: string; message: string };

// The company name is shared with the workspace (Settings, report "Issued by"), so those refresh too.
const INVALIDATES = [['my-risk'], ['vendors'], ['settings']];

const apiError = (e: any, fallback: string): string => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d.message === 'string') return d.message;
  return fallback;
};
const conflictOf = (e: any): Conflict | null => {
  const d = e?.response?.data?.detail;
  return e?.response?.status === 409 && d && d.code === 'vendor_exists' ? d : null;
};

const palette = (isDark: boolean) => ({
  isDark,
  ink: isDark ? '#e2e8f0' : T.ink,
  slate: isDark ? '#94a3b8' : T.slate,
  muted: isDark ? '#7c8aa0' : T.muted,
  line: isDark ? '#1f1f1f' : T.bdr,
  surface: isDark ? '#0d0d0d' : '#FFFFFF',
  surface2: isDark ? '#141414' : '#F8FAFC',
  bg: isDark ? '#000000' : T.bg,
  accent: isDark ? '#3B82F6' : '#2563EB',
  accentBg: isDark ? '#0f1e38' : '#EFF6FF',
  amber: isDark ? '#FACC15' : '#B45309',
  amberBg: isDark ? '#241f08' : '#FFFBEB',
  red: isDark ? '#F87171' : '#B91C1C',
});
type P = ReturnType<typeof palette>;

const input = (c: P): React.CSSProperties => ({
  width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 14, fontFamily: sans, boxSizing: 'border-box',
  border: `1px solid ${c.line}`, background: c.surface, color: c.ink, outline: 'none',
});
const button = (c: P, primary = false): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: primary ? '10px 18px' : '8px 14px',
  borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: sans, cursor: 'pointer', whiteSpace: 'nowrap',
  border: primary ? '1px solid transparent' : `1px solid ${c.line}`, background: primary ? c.accent : c.surface, color: primary ? '#fff' : c.ink,
});

const Icon = ({ d, size = 16 }: { d: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}><path d={d} /></svg>
);
const SHIELD = 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z';
const FEATURES = [
  { icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10ZM9 12l2 2 4-4', title: 'Security score and issues',
    body: 'The same 260+ external checks your vendors get, with a score, grade and prioritised findings.' },
  { icon: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20', title: 'Attack surface',
    body: 'Every subdomain and IP address we can find that belongs to you, and what each one exposes.' },
  { icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8', title: 'Who you depend on',
    body: 'The hosting, email, analytics and other providers visible behind your domain.' },
];

function Setup({ c, isAdmin, companyName, suggested, initialDomain }: {
  c: P; isAdmin: boolean; companyName: string; suggested: string | null; initialDomain?: string;
}) {
  const [name, setName] = useState(companyName);
  const [domain, setDomain] = useState(initialDomain ?? suggested ?? '');
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState<Conflict | null>(null);
  useEffect(() => { setName(companyName); }, [companyName]);

  const { refreshTenant } = useAuth();
  const save = useMutation({
    mutationFn: (adopt: boolean) => api.post('/v1/my-risk', { domain, name, adopt_existing: adopt }),
    meta: { invalidates: INVALIDATES },
    onSuccess: () => { refreshTenant().catch(() => undefined); },
  });
  const submit = (adopt = false) => {
    setError('');
    if (!domain.trim()) { setError("Enter your company's domain, e.g. acme.com"); return; }
    save.mutateAsync(adopt).then(() => setConflict(null)).catch(e => {
      const cf = conflictOf(e);
      if (cf) setConflict(cf); else setError(apiError(e, 'Could not set up your company profile. Try again.'));
    });
  };

  return (
    <div style={{ background: c.bg, minHeight: '100%', padding: '40px 16px', fontFamily: sans, display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 760 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: c.accentBg, color: c.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon d={SHIELD} size={22} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: c.muted }}>My Organization</div>
        </div>
        <h1 style={{ margin: '16px 0 0', fontSize: 26, fontWeight: 800, letterSpacing: '-.02em', color: c.ink }}>
          See how your own company looks from the outside
        </h1>
        <p style={{ margin: '8px 0 0', fontSize: 14.5, lineHeight: 1.6, color: c.slate, maxWidth: 640 }}>
          FluxWatch assesses your company exactly the way it assesses your vendors. This is the view your customers,
          partners and auditors get when they put you through their own vendor review.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, margin: '24px 0' }}>
          {FEATURES.map(f => (
            <div key={f.title} style={{ padding: 16, borderRadius: 12, border: `1px solid ${c.line}`, background: c.surface }}>
              <div style={{ color: c.accent }}><Icon d={f.icon} size={18} /></div>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: c.ink, marginTop: 10 }}>{f.title}</div>
              <div style={{ fontSize: 12.5, color: c.slate, marginTop: 4, lineHeight: 1.5 }}>{f.body}</div>
            </div>
          ))}
        </div>

        <div style={{ padding: 22, borderRadius: 12, border: `1px solid ${c.line}`, background: c.surface }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: c.ink }}>Set up your company profile</div>
          <div style={{ fontSize: 12.5, color: c.slate, marginTop: 3 }}>We scan the domain you enter now, then on your normal scan schedule.</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginTop: 18 }}>
            <label style={{ display: 'block' }}>
              <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: c.ink, marginBottom: 6 }}>Company name</span>
              <input value={name} onChange={e => setName(e.target.value)} disabled={!isAdmin} maxLength={200} style={input(c)} />
            </label>
            <label style={{ display: 'block' }}>
              <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: c.ink, marginBottom: 6 }}>Company domain</span>
              <input value={domain} onChange={e => { setDomain(e.target.value); setConflict(null); }} disabled={!isAdmin}
                placeholder="acme.com" onKeyDown={e => { if (e.key === 'Enter' && isAdmin) submit(); }} style={input(c)} />
            </label>
          </div>
          {suggested && domain === suggested && (
            <div style={{ fontSize: 12, color: c.muted, marginTop: 8 }}>Suggested from your work email. Change it if your website uses a different domain.</div>
          )}

          {conflict && (
            <div style={{ marginTop: 16, padding: 14, borderRadius: 10, background: c.amberBg, border: `1px solid ${c.amber}55` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.ink }}>{conflict.message}</div>
              <div style={{ fontSize: 12.5, color: c.slate, marginTop: 4, lineHeight: 1.5 }}>
                Use that record as your company profile? It moves out of your vendor list and keeps all of its scan history.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button style={button(c, true)} disabled={save.isPending} onClick={() => submit(true)}>
                  {save.isPending ? 'Moving…' : `Use ${conflict.vendor_name}`}
                </button>
                <button style={button(c)} onClick={() => setConflict(null)}>Cancel</button>
              </div>
            </div>
          )}
          {error && <div role="alert" style={{ marginTop: 12, fontSize: 12.5, color: c.red }}>{error}</div>}

          {!conflict && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
              {isAdmin ? (
                <button style={button(c, true)} disabled={save.isPending} onClick={() => submit()}>
                  <Icon d={SHIELD} size={15} /> {save.isPending ? 'Starting…' : 'Scan my company'}
                </button>
              ) : (
                <span style={{ fontSize: 12.5, color: c.slate }}>Only an administrator can set your company domain.</span>
              )}
              {isAdmin && <span style={{ fontSize: 12, color: c.muted }}>The first scan takes a few minutes. You can keep working meanwhile.</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChangeDomain({ c, profile, onClose }: { c: P; profile: { name: string; domain: string }; onClose: () => void }) {
  const [name, setName] = useState(profile.name);
  const [domain, setDomain] = useState(profile.domain);
  const [error, setError] = useState('');
  const { refreshTenant } = useAuth();
  const save = useMutation({
    mutationFn: () => api.post('/v1/my-risk', { domain, name }),
    meta: { invalidates: INVALIDATES },
    onSuccess: () => { refreshTenant().catch(() => undefined); },
  });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const changing = domain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '') !== profile.domain;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 16 }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Company profile" style={{ width: '100%', maxWidth: 460, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12, padding: 20, fontFamily: sans }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: c.ink }}>Company profile</div>
        <div style={{ display: 'grid', gap: 12, marginTop: 14 }}>
          <label><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: c.ink, marginBottom: 6 }}>Company name</span>
            <input value={name} onChange={e => setName(e.target.value)} maxLength={200} style={input(c)} /></label>
          <label><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: c.ink, marginBottom: 6 }}>Company domain</span>
            <input value={domain} onChange={e => setDomain(e.target.value)} style={input(c)} /></label>
        </div>
        {changing && (
          <div style={{ marginTop: 12, padding: 10, borderRadius: 8, background: c.amberBg, fontSize: 12.5, color: c.ink, lineHeight: 1.5 }}>
            Changing the domain starts a fresh scan history for the new domain. Earlier scans of {profile.domain} stay in your scan list.
          </div>
        )}
        {error && <div role="alert" style={{ marginTop: 10, fontSize: 12.5, color: c.red }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          <button style={button(c)} onClick={onClose}>Cancel</button>
          <button style={button(c, true)} disabled={save.isPending}
            onClick={() => save.mutateAsync().then(onClose).catch(e => setError(apiError(e, 'Could not save. Try again.')))}>
            {save.isPending ? 'Saving…' : changing ? 'Save and rescan' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyRisk() {
  const { user } = useAuth();
  const { mode } = useCustomTheme();
  const c = palette(mode === 'dark');
  const isAdmin = !!user && (user.role === 'admin' || user.role === 'super_admin');
  const status = useQuery(myRiskQueries.status());
  const [editing, setEditing] = useState(false);

  if (status.isPending) {
    return <div style={{ padding: 40, fontFamily: sans, fontSize: 13, color: c.slate, background: c.bg, minHeight: '100%' }}>Loading…</div>;
  }
  if (status.isError || !status.data) {
    return (
      <div style={{ padding: 40, fontFamily: sans, background: c.bg, minHeight: '100%' }}>
        <div style={{ fontSize: 13, color: c.red }}>Could not load your company profile.</div>
        <button style={{ ...button(c), marginTop: 12 }} onClick={() => status.refetch()}>Try again</button>
      </div>
    );
  }

  const { profile, company_name, suggested_domain } = status.data;
  if (!profile) {
    return <Setup c={c} isAdmin={isAdmin} companyName={company_name || ''} suggested={suggested_domain} />;
  }

  return (
    <>
      {status.data.scan_in_progress && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', background: c.accentBg, color: c.ink, fontFamily: sans, fontSize: 13, borderBottom: `1px solid ${c.line}` }}>
          <span style={{ width: 8, height: 8, borderRadius: 99, background: c.accent, animation: 'myRiskPulse 1.2s ease-in-out infinite alternate' }} />
          <style>{'@keyframes myRiskPulse { from { opacity: .35 } to { opacity: 1 } }'}</style>
          Scanning {profile.domain}. Your score and findings update here automatically when it finishes.
        </div>
      )}
      <VendorDetails
        key={profile.vendor_id}
        selfVendorId={profile.vendor_id}
        selfActions={isAdmin ? (
          <button style={{ ...button(c), padding: '7px 12px', fontSize: 12 }} onClick={() => setEditing(true)}>Company profile</button>
        ) : null}
      />
      {editing && <ChangeDomain c={c} profile={profile} onClose={() => setEditing(false)} />}
    </>
  );
}
